import type { PaymentInfo } from '@festina/moneyin-general-types/moneyin'
import { amountUtils, notEmpty } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import { Card, CardBody } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useMemo, useState } from 'react'

import { ErrorMessage } from '../../components/errorFallback/ErrorMessage'
import { LifeErrorFallback } from '../../components/errorFallback/LifeErrorFallback'
import { LifePage } from '../../components/page/LifePage'
import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { useComponent, useHook } from '../../lenteHooks'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { MatchPaymentCard } from './paymentInfoTable/MatchPaymentCard'
import type { ReceivablesInfoWithTotal } from './receivablesTable/types'

const MatchPageInternal = () => {
  const translate = useTranslate()
  const { formatWithDecimals } = useAmountFormatter()
  const tenantId = useHook('routing.tenant.useTenantParam')()

  const [paymentsBeingAttached, setPaymentsBeingAttached] = useState<
    PaymentInfo[]
  >([])
  const [receivablesBeingAttached, setReceivablesBeingAttached] =
    useState<ReceivablesInfoWithTotal>()

  const MatchDebtCard = useComponent('pages.moneyIn.MatchDebtCard')
  const { mutateAsync, isPending, error } = useHook(
    'mutators.moneyIn.useSubmitPaymentReconciliation',
  )({
    tenantId,
  })

  const handleSubmit = async () => {
    const paymentIds =
      paymentsBeingAttached
        ?.map((payment) => payment.paymentId)
        .filter(notEmpty) ?? []

    await mutateAsync({
      requestBody: {
        _type: 'ReconcilePaymentsData',
        paymentIds,
        receivablesInfo: receivablesBeingAttached?.receivablesInfo,
      },
    })

    setPaymentsBeingAttached([])
    setReceivablesBeingAttached(undefined)
  }

  const netAmount = useMemo(() => {
    const paymentAmount =
      paymentsBeingAttached
        ?.map((payment) => payment.amount)
        .filter(notEmpty)
        .reduce((sum, amount) => sum + amount, 0) ?? 0

    return amountUtils.roundToPrecision(
      paymentAmount - (receivablesBeingAttached?.totalAmount ?? 0),
      2,
    )
  }, [paymentsBeingAttached, receivablesBeingAttached])

  return (
    <LifePage>
      <ErrorMessage error={error} className="shrink-0" />
      <div
        // Grow from min-h-0 to not outgrow parent flex container, and ensure any direct children doesn't outgrow this grid container
        className="grid gap-2 grid-cols-2 grow *:min-h-0 min-h-0"
      >
        <MatchPaymentCard
          selectedPaymentInfos={paymentsBeingAttached}
          onSelectedPaymentInfosChanged={setPaymentsBeingAttached}
        />
        <MatchDebtCard
          selectedReceivablesInfo={receivablesBeingAttached}
          onSelectedReceivablesInfoChanged={setReceivablesBeingAttached}
        />
      </div>
      <div className="flex gap-2 shrink-0">
        {/* TODO: Find better way to communicate success than hardcoding background color of card */}
        <Card
          className={cx('grow', {
            'bg-error-300': netAmount !== 0,
            'bg-success-300': netAmount === 0,
          })}
        >
          <CardBody>
            {translate('moneyIn.match.netAmount')}:{' '}
            {formatWithDecimals(netAmount)}
          </CardBody>
        </Card>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={isPending}
          disabled={!paymentsBeingAttached.length || !receivablesBeingAttached}
        >
          {translate('moneyIn.match.action.match')}
        </Button>
      </div>
    </LifePage>
  )
}

export const MatchPage = withLifeErrorBoundary(
  withSuspense(MatchPageInternal),
  {
    FallbackComponent: LifeErrorFallback,
  },
)
